// 通話機能の console-page への「置くだけ」結線（設計v2 §2.6・AC-14）。
// console-page 側の変更は import＋<CallButton/>＋<CallDock/>＋SSE の CustomEvent 1行のみに抑え、
// 着信トリガ購読・deep-link 遷移・パネル/モーダル描画はすべてここに閉じる。

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CallPanel } from './call-panel';
import { IncomingCall } from './incoming-call';
import { useCallStore, startCall, ringIncoming, wireCallRuntime, type IncomingCallInfo } from './call-store';

/** スレッドヘッダ右肩の発信ボタン（AC-1） */
export function CallButton({ conversationId, pcOnline }: { conversationId: string | null; pcOnline: boolean }) {
  const phase = useCallStore((s) => s.phase);
  const voiceOnline = useCallStore((s) => s.voiceOnline);
  const inCall = phase !== 'idle';
  const disabled = inCall || !conversationId || !pcOnline;
  const title = inCall
    ? '通話中です'
    : !conversationId
      ? 'スレッドを選ぶと発信できます'
      : !pcOnline
        ? '応答できるPC（DIR）がオフラインです'
        : voiceOnline
          ? 'この会話の AI と通話する'
          : '発信（⚠ 音声サーバ未接続の可能性）';
  return (
    <button
      className="btn-ghost"
      type="button"
      disabled={disabled}
      onClick={() => void startCall(conversationId)}
      data-testid="call-button"
      title={title}
      style={{ fontSize: 16 }}
    >
      📞{!voiceOnline && !inCall ? '⚠' : ''}
    </button>
  );
}

// push relay payload（public/sw.js が clients.postMessage する形）
interface PushRelay {
  type?: string;
  payload?: {
    title?: string;
    body?: string;
    data?: { conversationId?: string; projectId?: number | string; teamId?: number | string };
  };
}

/** パネル・着信モーダルのマウント＋着信トリガ購読（AC-4/5/6） */
export function CallDock() {
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    wireCallRuntime();

    // 主経路: 完了 Web Push → SW relay（public/sw.js の clients.postMessage）→ 着信リング
    const onSwMessage = (e: MessageEvent) => {
      const msg = e.data as PushRelay;
      if (!msg || msg.type !== 'konecco-push') return;
      const conv = msg.payload?.data?.conversationId;
      if (typeof conv !== 'string' || conv === '') return;
      const pj = Number(msg.payload?.data?.projectId);
      const team = Number(msg.payload?.data?.teamId);
      ringIncoming(
        {
          conversationId: conv,
          title: msg.payload?.title,
          projectId: Number.isFinite(pj) ? pj : undefined,
          teamId: Number.isFinite(team) ? team : undefined,
          source: 'push',
        },
        // 同一会話の連続 push は 5s 窓で 1 回に抑え、以後の完了では再度鳴らせる
        `push:${conv}:${Math.floor(Date.now() / 5000)}`,
      );
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);

    // 副経路: アクティブ会話の SSE（console-page が CustomEvent で中継）で非同期完了を検知。
    // 非同期完了は BE から `update`(streamState:'final', msgKind:'final') で届く（whole `message`
    // ではない＝admin.service finalizeStreamingMessage）。両形とも拾って着信リングにする。
    const onThreadEvent = (e: Event) => {
      const ev = (e as CustomEvent).detail as {
        kind?: string;
        conversationId?: string;
        id?: number;
        streamState?: string;
        msgKind?: string; // FE 型未宣言だが BE は update に載せる
        message?: { id?: number; role?: string; msgKind?: string };
      };
      if (!ev || typeof ev.conversationId !== 'string') return;
      const isAsyncFinal =
        (ev.kind === 'update' && ev.streamState === 'final' && ev.msgKind === 'final') ||
        (ev.kind === 'message' && ev.message?.role === 'ai' && ev.message?.msgKind === 'final');
      if (!isAsyncFinal) return;
      ringIncoming(
        { conversationId: ev.conversationId, source: 'sse' },
        `sse:${ev.conversationId}:${ev.id ?? ev.message?.id ?? ''}`,
      );
    };
    window.addEventListener('konecco:thread-event', onThreadEvent);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
      window.removeEventListener('konecco:thread-event', onThreadEvent);
    };
  }, []);

  // 受諾時: 会話を開く（既存 ?open= deep-link 規約に乗せる。pj 不明なら遷移せず通話のみ開始）
  const onAccepted = (info: IncomingCallInfo) => {
    if (info.projectId != null) {
      setSearchParams({
        open: info.conversationId,
        pj: String(info.projectId),
        ...(info.teamId != null ? { team: String(info.teamId) } : {}),
      });
    }
  };

  return (
    <>
      <CallPanel />
      <IncomingCall onAccepted={onAccepted} />
    </>
  );
}
